/* eslint-disable @reatom/unit-naming-rule */
import { atom, onConnect } from '@reatom/framework'

import { Array, AsyncStateN, pipe, Result } from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth'
import { selectedNetworkAtom } from '@root/entities/network'
import { reatomOrder } from '@root/entities/order/atoms'
import { fetchUserOrdersRequestAction } from '@root/entities/order/atoms/order-requests'
import { pairRegistry, type PairAddress } from '@root/entities/pair'
import { userOrderEvent } from '@root/entities/realtime'
import { deviceWalletIdsAtom } from '@root/features/secure-storage/atoms'
import { dataTableNoAccessError, reatomDataTable } from '@root/shared/ui-kit'

import { createOrdersTableColumns } from '../ui/orders-table-columns'

import type { OrderModel } from '@root/entities/order/atoms'
import type { TableData } from '@root/shared/ui-kit'

export const reatomOrdersTable = (pairAddress: PairAddress) => {
  const paramsAtom = atom((ctx) => {
    const walletIds = ctx.spy(deviceWalletIdsAtom)
    const network = ctx.spy(selectedNetworkAtom)
    return { walletIds, network }
  }, 'paramsAtom')

  const tableModel = reatomDataTable({
    columns: createOrdersTableColumns({ withCurrency: false }),
    paramsAtom,

    name: 'orders-table',
    loadItems: async (
      ctx,
      { params: { network, walletIds }, pagination: { limit, offset } },
    ) => {
      const isLoggedIn = ctx.get(isLoggedInAtom)
      if (!isLoggedIn) {
        return Result.fail(dataTableNoAccessError())
      }

      const result = await fetchUserOrdersRequestAction(ctx, {
        limit,
        offset,
        network,
        pairAddress,
        walletIds: [...walletIds],
        sort: 'desc',
      })

      return pipe(
        result,
        Result.map((data): TableData<OrderModel> => {
          const mappedData = pipe(
            data.data.map((dto) => {
              // TODO: Support orders without pair meta
              if (!dto.pairMeta) {
                return undefined
              }
              const pair = pairRegistry.resolveFromDto(ctx, dto.pairMeta)
              const order = reatomOrder({ dto, pair })
              return order
            }),
            Array.compact,
          )
          return {
            data: mappedData,
            hasNext: data.meta.hasNext,
          }
        }),
      )
    },
    limit: 20,
    getId: (item) => item.order.id,
  })

  onConnect(tableModel, () => {
    const unsubscribe = userOrderEvent.onCall((ctx, orderUpdate) => {
      if (orderUpdate.order.pairMeta?.poolAddress !== pairAddress) {
        return
      }
      const state = ctx.get(tableModel)
      if (!AsyncStateN.isSuccess(state)) {
        return
      }
      const items = state.value.data
      if (items.some((it) => it.order.id === orderUpdate.order.id)) {
        return
      }
      tableModel.refresh(ctx)
    })

    return () => {
      unsubscribe()
    }
  })

  return tableModel
}

export type OrdersTableModel = ReturnType<typeof reatomOrdersTable>
